import { AnnotatedType, PrimaryAnnotatedType } from '@/components/annotated-type';
import { HorizontalBarChart } from '@/components/bar-chart/horizontal';
import type { BarChartItem } from '@/components/bar-chart/vertical';
import { StoryQuestion, useStoryQuestion } from '@/components/microstory/question';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { select, t, Trans } from '@lingui/macro';
import { useState } from 'react';

type EqualityForSomeChapter3Props = {
  data: EqualityForSomeData['lgbtq_equality_young_gen'];
};

export function EqualityForSomeChapter3(props: EqualityForSomeChapter3Props) {
  const { data } = props;

  const formatValue = useNumberFormatting('percent');
  const [chartVisible, setChartVisible] = useState(false);

  const [chartData, setChartData] = useState<BarChartItem[]>([
    {
      label: t({
        id: 'equality-for-some.chapter-3.chart-label-1',
        message: 'Young women',
      }),
      value: data.female,
    },
    {
      label: t({ id: 'equality-for-some.chapter-3.chart-label-2', message: 'Young men' }),
      value: data.male,
    },
  ]);

  const { selectedAnswer, handleAnswer } = useStoryQuestion();

  const answers = [
    {
      id: 'youngWomen',
      label: t({ id: 'equality-for-some.chapter-3.question.answer-1', message: 'Young women' }),
    },
    {
      id: 'youngMen',
      label: t({ id: 'equality-for-some.chapter-3.question.answer-2', message: 'Young men' }),
    },
    {
      id: 'equal',
      label: t({
        id: 'equality-for-some.chapter-3.question.answer-3',
        message: 'Young women and men equally',
      }),
    },
  ];

  const handleStepEnter = async (evt: OnStepEnterExitArgs<BarChartItem[]>) => {
    setChartVisible(true);
    if (evt.data) setChartData(evt.data);
  };

  return (
    <>
      <StoryQuestion
        question={t({
          id: 'equality-for-some.chapter-3.question.question',
          message:
            'Among young people, who do you think expresses most support for the equal treatment of LGBTQ+ people?',
        })}
        answers={answers}
        onAnswer={handleAnswer}
        selectedAnswer={selectedAnswer}
        response={
          selectedAnswer?.id &&
          t({
            id: 'equality-for-some.chapter-3.question.response',
            message: select(selectedAnswer.id, {
              youngWomen: "That's right!",
              youngMen: 'Afraid not!',
              equal: 'Afraid not!',
            }),
          })
        }
      />

      <ScrollRoot>
        <ScrollGraphic>
          <HorizontalBarChart
            label={t({
              id: 'equality-for-some.chapter-4.chart-label',
              message: '% who say equal treatment of LGBTQ+ people is very important',
            })}
            data={chartData}
            visible={chartVisible}
            formatValue={formatValue}
          />
        </ScrollGraphic>

        <ScrollSteps offset={0.7} onStepEnter={handleStepEnter}>
          <ScrollStep
            data={[
              {
                label: t({
                  id: 'equality-for-some.chapter-3.chart-label-1',
                  message: 'Young women',
                }),
                value: data.female,
              },
              {
                label: t({ id: 'equality-for-some.chapter-3.chart-label-2', message: 'Young men' }),
                value: data.male,
              },
            ]}
          >
            <StoryText>
              <Trans id="equality-for-some.chapter-3.text-1">
                On average, young women are more likely than young men to say that{' '}
                <PrimaryAnnotatedType>equal treatment of LGBTQ+ people</PrimaryAnnotatedType> is
                very important by about {Math.round(data.diff * 100)} percentage points.
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep>
            <StoryText>
              <Trans id="equality-for-some.chapter-3.text-2">
                Of all the questions in our poll, this one reveals the{' '}
                <AnnotatedType>biggest difference</AnnotatedType> between the sexes within the young
                generation.
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep>
            <StoryText>
              <Trans id="equality-for-some.chapter-3.text-3">
                These findings echo others in our survey: Young women overall express{' '}
                <AnnotatedType>
                  greater concern for equal treatment and for fighting discrimination
                </AnnotatedType>{' '}
                than young men.
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>
    </>
  );
}
